import Constants from 'src/core/utils/Constants';
import { youtubeVideoId } from 'src/core/utils/Youtube';
import { type FC, useState } from 'react';
import { Dimensions, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { unstable_createElement as createElement } from 'react-native-web';
import Colors from 'src/core/utils/Colors';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';
import { SvgIconType } from '@thermenresorts/icons';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import Translations from 'src/core/utils/Translations';

const { width } = Dimensions.get('window');

const CARD_HORIZONTAL_INSET = Dimens.padding18 * 2;
const CARD_HEIGHT = (width - CARD_HORIZONTAL_INSET) / Dimens.aspectRatio16By9;

const TYPE_LABEL_HEIGHT = Dimens.size18;
const TYPE_LABEL_OFFSET = (CARD_HEIGHT - TYPE_LABEL_HEIGHT) / 2 - Dimens.padding16;

interface Props {
	title: string;
	label: string;
	imageUrl: string | null;
	contentUrl: string;
	isYouTubeUrl?: boolean | undefined;
}

const InspirationCard: FC<Props> = ({ title, label, imageUrl, contentUrl, isYouTubeUrl }: Props) => {
	const { openUrlExternally } = useServicesStore();

	const [showVideo, setShowVideo] = useState(false);
	const [playing, setPlaying] = useState(false);

	const onPress = () => {
		if (isYouTubeUrl) {
			if (playing) {
				return;
			}
			setShowVideo(true);
			setPlaying(true);
		} else {
			openUrlExternally({ url: contentUrl });
		}
	};

	const embedUrlOf = (url: string) => {
		const videoId = youtubeVideoId(url);

		return videoId ? `${Constants.urlYoutubeEmbed}${videoId}?autoplay=1` : null;
	};

	return (
		<TouchableOpacity activeOpacity={Dimens.opacity80} onPress={onPress}>
			<View style={styles.card}>
				<View style={styles.imageContainer}>
					{showVideo ? (
						<View style={styles.cardImage}>
							{createElement('iframe', {
								title,
								src: embedUrlOf(contentUrl),
								height: CARD_HEIGHT,
								allowFullScreen: true,
								allow: 'autoplay',
							})}
						</View>
					) : (
						<ThermenImage contentFit={'cover'} source={imageUrl ? { uri: imageUrl } : null} style={styles.image} />
					)}
					{!showVideo && isYouTubeUrl && (
						<>
							<View style={styles.imageOverlay}>
								<View style={styles.play}>
									<SvgIcon name={SvgIconType.play} size={Dimens.iconSize12} tintColor={Colors.white} />
								</View>
							</View>
							<View style={styles.typeContainer}>
								<View style={styles.line} />
								<Text style={styles.label}>{label}</Text>
							</View>
						</>
					)}
				</View>
				<View style={styles.content}>
					<Text style={styles.title}>{title}</Text>
					{!isYouTubeUrl && (
						<TouchableOpacity onPress={onPress}>
							<Text style={styles.readMoreLink}>{Translations.readMore}</Text>
						</TouchableOpacity>
					)}
				</View>
			</View>
		</TouchableOpacity>
	);
};

const styles = StyleSheet.create({
	cardImage: {
		width: '100%',
		height: CARD_HEIGHT,
	},
	card: {
		flex: Dimens.flex1,
		backgroundColor: Colors.white,
		flexDirection: 'column',
		borderRadius: Dimens.borderRadius4,
	},
	content: {
		paddingHorizontal: Dimens.padding16,
		paddingVertical: Dimens.padding20,
	},
	title: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize20,
		lineHeight: Dimens.lineHeight30,
		color: Colors.gray800,
	},
	readMoreLink: {
		fontSize: Dimens.fontSize16,
		lineHeight: Dimens.lineHeight18,
		color: Colors.green,
		textDecorationLine: 'underline',
		marginTop: Dimens.padding12,
	},
	image: {
		width: '100%',
		height: CARD_HEIGHT,
		borderTopRightRadius: Dimens.borderRadius4,
		borderTopLeftRadius: Dimens.borderRadius4,
	},
	imageContainer: {
		position: 'relative',
		backgroundColor: Colors.black,
	},
	imageOverlay: {
		position: 'absolute',
		top: Dimens.padding0,
		left: Dimens.padding0,
		right: Dimens.padding0,
		bottom: Dimens.padding0,
		backgroundColor: Colors.inspirationCardOverlay,
		justifyContent: 'center',
		alignItems: 'center',
	},
	typeContainer: {
		position: 'absolute',
		width: CARD_HEIGHT,
		height: TYPE_LABEL_HEIGHT,
		top: TYPE_LABEL_OFFSET,
		right: -TYPE_LABEL_OFFSET,
		flex: Dimens.flex1,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: Dimens.gap5,
		transform: [
			{
				rotate: `${Dimens.rotate90}deg`,
			},
		],
	},
	line: {
		height: Dimens.size1,
		width: Dimens.size56,
		backgroundColor: Colors.white,
	},
	play: {
		width: Dimens.size50,
		height: Dimens.size50,
		borderRadius: Dimens.borderRadius25,
		alignItems: 'center',
		justifyContent: 'center',
		borderWidth: Dimens.borderWidth2,
		borderColor: Colors.white66,
		paddingLeft: Dimens.padding2,
	},
	label: {
		color: Colors.white,
		textTransform: 'uppercase',
		fontSize: Dimens.fontSize14,
		lineHeight: Dimens.lineHeight17,
		fontFamily: Fonts.barlowMedium,
	},
});

export default InspirationCard;
